import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bug, Sparkles, MessageSquare, Loader2 } from "lucide-react";

const CAT_META = {
  bug:             { label: "Bug",             icon: Bug,           tone: "text-red-700 bg-red-50 border-red-200" },
  feature_request: { label: "Feature Request", icon: Sparkles,      tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  general:         { label: "General",         icon: MessageSquare, tone: "text-slate-700 bg-slate-50 border-slate-200" },
};
const STATUSES = ["NEW", "REVIEWED", "PLANNED", "RESOLVED", "DECLINED"];
const STATUS_TONE = {
  NEW:      "bg-amber-50 text-amber-800 border-amber-200",
  REVIEWED: "bg-blue-50 text-blue-800 border-blue-200",
  PLANNED:  "bg-indigo-50 text-indigo-800 border-indigo-200",
  RESOLVED: "bg-green-50 text-green-800 border-green-200",
  DECLINED: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function AdminFeedback() {
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = useCallback(async (append = false, cursor = null) => {
    setLoading(true); setError("");
    try {
      const params = { limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (cursor) params.cursor = cursor;
      const r = await api.get("/feedback", { params });
      setRows((prev) => append ? [...prev, ...(r.data.results || [])] : (r.data.results || []));
      setNextCursor(r.data.next_cursor || null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to load feedback.");
    } finally { setLoading(false); }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(false, null); }, [load]);

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/feedback/${id}`, { status: next });
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: next } : r));
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to update status.");
    }
  };

  const total = rows.length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-feedback-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-serif font-black tracking-tight">User Feedback</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bug reports, feature requests, and general notes from real DocCircle users.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="admin-feedback-filters">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setStatusFilter("")}
            data-testid="admin-feedback-status-all"
            className={`text-xs px-3 py-1 rounded-full border ${!statusFilter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >All statuses</button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-feedback-status-${s}`}
              className={`text-xs px-3 py-1 rounded-full border ${statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >{s}</button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategoryFilter("")}
            data-testid="admin-feedback-cat-all"
            className={`text-xs px-3 py-1 rounded-full border ${!categoryFilter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >All types</button>
          {Object.entries(CAT_META).map(([k, m]) => (
            <button
              key={k}
              type="button"
              onClick={() => setCategoryFilter(k)}
              data-testid={`admin-feedback-cat-${k}`}
              className={`text-xs px-3 py-1 rounded-full border ${categoryFilter === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >{m.label}</button>
          ))}
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="admin-feedback-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {error && <div className="text-sm text-red-600" data-testid="admin-feedback-error">{error}</div>}
      {!loading && !error && total === 0 && (
        <Card data-testid="admin-feedback-empty">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No feedback matches these filters yet.
          </CardContent>
        </Card>
      )}

      {rows.length > 0 && (
        <div className="space-y-3" data-testid="admin-feedback-list">
          {rows.map((r) => {
            const meta = CAT_META[r.category] || CAT_META.general;
            const Icon = meta.icon;
            return (
              <div
                key={r.id}
                data-testid={`admin-feedback-item-${r.id}`}
                className="border border-border rounded-lg p-4 bg-card space-y-3"
              >
                <div className="flex items-start gap-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border ${meta.tone}`}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                  <span className={`inline-flex items-center text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_TONE[r.status] || ""}`}>
                    {r.status}
                  </span>
                  <span className="text-[11px] text-muted-foreground ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                {/* Message renders as a React text node — no dangerouslySetInnerHTML. */}
                <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid={`admin-feedback-message-${r.id}`}>{r.message}</p>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  {r.page && <span>Page: <span className="font-mono">{r.page}</span></span>}
                  <span>Contact: {r.contact_allowed ? "allowed" : "not permitted"}</span>
                  {r.submitter_display_name && <span>From: {r.submitter_display_name}</span>}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => updateStatus(r.id, s)}
                      disabled={r.status === s}
                      data-testid={`admin-feedback-set-${r.id}-${s}`}
                      className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${
                        r.status === s
                          ? "bg-secondary text-secondary-foreground border-secondary cursor-default"
                          : "border-border text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {nextCursor && (
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              data-testid="admin-feedback-load-more"
              onClick={() => load(true, nextCursor)}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
